<!-- src/components/StraitMapStepScroller.svelte
     v11 step 4 — Scroller 重寫,完全對齊日本原版結構
     職責:
       1. 渲染整個 scrollytelling 容器(sticky 地圖 + 觸發盒序列)
       2. 把每個觸發盒對應的 ScriptStore 觸發函式 attach 到 ScrollTrigger
       3. ScrollTrigger 配置(start / end / onEnter 等)
     不做地圖邏輯 — 全部委託給 StraitMap.svelte 的四個 API
-->
<script>
  import { onMount, tick } from 'svelte'
  import { gsap } from 'gsap'
  import { ScrollTrigger } from 'gsap/ScrollTrigger'

  import StraitMap from './StraitMap.svelte'
  import IntroTriggerBox from '../shared/IntroTriggerBox.svelte'
  import FullTriggerBox from '../shared/FullTriggerBox.svelte'
  import EmptyTriggerBox from '../shared/EmptyTriggerBox.svelte'

  import {
    introStepInfo,
    fullContentStepInfo,
    emptyBoxStepInfo,
  } from '../stores/StraitScriptStore.js'
  import ContentDataStore from '../stores/ContentDataStore.js'
  import { sceneOrder } from '../data/straitScenes.js'

  gsap.registerPlugin(ScrollTrigger)

  let triggersRegistered = false

  // 取出每個海峽的故事陣列(維持 sceneOrder 順序),保留 sceneId 用於跳轉錨點
  $: storyList = $ContentDataStore
    ? sceneOrder
        .map((id) => ({ sceneId: id, story: $ContentDataStore[`scroll_${id}`] }))
        .filter(s => s.story)
    : []

  // 等內容載入 + DOM 渲染完成後,註冊 ScrollTrigger
  // 注意:必須等 introStepInfo / fullContentStepInfo / emptyBoxStepInfo 都填好
  $: if (
    storyList.length > 0 &&
    $introStepInfo.length > 0 &&
    $fullContentStepInfo.length > 0 &&
    !triggersRegistered
  ) {
    triggersRegistered = true
    tick().then(() => {
      // 多等一個 frame 確保 each loop 渲染完成
      setTimeout(registerScrollTriggers, 100)
    })
  }

  function registerScrollTriggers() {
    // intro 觸發盒
    gsap.utils.toArray('.intro-trigger-wrap').forEach((elm, i) => {
      const handlers = $introStepInfo[i]
      if (!handlers) return
      ScrollTrigger.create({
        trigger: elm,
        start: 'top center',
        end: 'bottom center',
        onEnter:     () => handlers.onEnter?.(),
        onLeave:     () => handlers.onLeave?.(),
        onEnterBack: () => handlers.onEnterBack?.(),
        onLeaveBack: () => handlers.onLeaveBack?.(),
      })
    })

    // full_content 觸發盒
    // [v13.1] start 從 'top center' → 'top 80%' 提早觸發
    //   原本要等觸發盒上緣到視窗中心才啟動,step layer fade-in 跟
    //   flyToBbox 加起來 ~1100ms,使用者看到蓋板文字時島鏈/管線還在 fade。
    //   改成 'top 80%' (上緣到視窗下方 20% 位置就觸發),提早 ~30vh 的
    //   滾動距離 = 多 0.5-1 秒給 layer 完整顯示。
    gsap.utils.toArray('.full-trigger-wrap').forEach((elm, i) => {
      const handlers = $fullContentStepInfo[i]
      if (!handlers) return
      ScrollTrigger.create({
        trigger: elm,
        start: 'top bottom',
        end: 'bottom 10%',
        onEnter:     () => handlers.onEnter?.(),
        onLeave:     () => handlers.onLeave?.(),
        onEnterBack: () => handlers.onEnterBack?.(),
        onLeaveBack: () => handlers.onLeaveBack?.(),
        onUpdate: ({ progress }) => handlers.onUpdate?.(progress),
      })
    })

    // empty (path_transition) 觸發盒
    // 規則:首尾各有一個防護 spacer(沒對應 emptyBoxStepInfo 條目),用 slice 去頭去尾
    const allEmpty = gsap.utils.toArray('.empty-trigger-wrap')
    const middleEmpties = allEmpty.slice(1, allEmpty.length - 1)
    middleEmpties.forEach((elm, i) => {
      const handlers = $emptyBoxStepInfo[i]
      if (!handlers) return
      ScrollTrigger.create({
        trigger: elm,
        start: 'top top',
        end: 'bottom bottom',
        onEnter:     () => handlers.onEnter?.(),
        onLeave:     () => handlers.onLeave?.(),
        onEnterBack: () => handlers.onEnterBack?.(),
        onLeaveBack: () => handlers.onLeaveBack?.(),
        onUpdate: ({ progress }) => handlers.onUpdate?.(progress),
      })
    })
  }
</script>

<style>
  .scene-wrap {
    position: relative;
  }
  .map-stage {
    position: sticky;
    top: 0;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  .step-trigger-container {
    position: relative;
    width: 100%;
    z-index: 10;
    pointer-events: none;
  }
</style>

<section class="scene-wrap" id="strait-scrollytelling">
  <!-- ====== sticky 地圖場景(StraitMap 自己內含 #full-box-bg + #intro-curtain) ====== -->
  <div class="map-stage">
    <StraitMap />
  </div>

  <!-- ====== 觸發盒序列(對齊日本原版的結構) ====== -->
  <div class="step-trigger-container">
    <!-- 進站防護 spacer:避免第一個 intro 在頁面剛載入時立刻觸發 -->
    <EmptyTriggerBox boxHeight={100} />

    {#each storyList as { sceneId, story }}
      <!-- ⭐ scroll anchor:讓 StraitJumpNav 跳轉用 -->
      <div id="scroll-anchor-{sceneId}"></div>
      {#each story as item}
        {#if item.type === 'intro'}
          <IntroTriggerBox textContent={item.content} />
        {:else if item.type === 'full_content'}
          <FullTriggerBox
            title={item.title}
            lede={item.lede}
            content={item.content}
            recommendation={item.recommendation}
          />
        {:else if item.type === 'path_transition'}
          <EmptyTriggerBox boxHeight={+item.content || 200} />
        {/if}
      {/each}
    {/each}

    <!-- 出站防護 spacer:讓最後一張 full_content 完整滾出視窗 -->
    <EmptyTriggerBox boxHeight={150} />
  </div>
</section>
